import type { ChangeEvent } from 'react';
import { getWeekDayWide, useLocale } from '../../../../utils/date-fns';
import { CALENDAR_SETTINGS } from '../../constants';
import { militaryToText } from '../../utils';

const formRowClasses = 'mb-4 flex items-center justify-between';
const formLabelClasses = 'block text-sm';

type TimeInputChange = {
    target: {
        name: string;
        value: number;
    };
};

interface TimeInputProps {
    label: string;
    time: number;
    onChange: (event: TimeInputChange) => void;
    name: string;
    error?: string | null;
    onDayChange: (day: number) => void;
    day: number;
    dayError?: string | null;
}

export function TimeInput({
    label,
    time,
    onChange,
    name,
    error,
    onDayChange,
    day,
    dayError,
}: TimeInputProps): JSX.Element {
    const locale = useLocale();
    const daysOfWeek = Array.from({ length: CALENDAR_SETTINGS.DAYS_IN_WEEK }, (_, i) =>
        getWeekDayWide(locale, i)
    );

    const timeInput = militaryToText(time);

    function timeChanged(event: ChangeEvent<HTMLInputElement>): void {
        const { name: targetName, value } = event.target;
        const newValue = parseInt(value.replace(':', ''), 10);
        onChange({ target: { name: targetName, value: newValue } });
    }

    return (
        <div className={formRowClasses}>
            <label className={formLabelClasses}>{label}</label>
            <div>
                <div className="flex">
                    <select
                        className={`mr-8 w-32 rounded-lg border px-3 py-2 text-sm ${
                            dayError ? 'border-red-500' : ''
                        }`}
                        value={day}
                        onChange={(e) => onDayChange(parseInt(e.target.value, 10))}
                    >
                        {daysOfWeek.map((dayName, index) => (
                            <option key={dayName} value={index}>
                                {dayName}
                            </option>
                        ))}
                    </select>
                    <input
                        className={`w-32 cursor-text rounded-lg border py-2 pl-11 pr-3 text-sm ${
                            error ? 'border-red-500' : ''
                        }`}
                        name={name}
                        onChange={timeChanged}
                        value={timeInput}
                        type="time"
                    />
                </div>
                {(error || dayError) && (
                    <div className="float-right text-red-600" data-testid="time-input-error">
                        {dayError && <p data-testid="day-error">{dayError}</p>}
                        {error && <p data-testid="time-error">{error}</p>}
                    </div>
                )}
            </div>
        </div>
    );
}
